import { useDisclosure } from '@mantine/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { MUTATION, useMutationOperation } from 'api/Mutation';
import { QUERY, type QueryOperation } from 'api/Query';
import type { UnresolvedCommitDescriptor } from 'custom-types/UnresolvedCommitDescriptor';
import { useProjectId } from 'ts/base/hooks/UseProject';
import { useUserInfo } from 'ts/base/hooks/UserInfoHook';
import { ObjectUtils } from 'ts/commons/ObjectUtils';
import type { ButtonProps } from 'ts/components/Button';
import { Button } from 'ts/components/Button';
import { isPending } from 'ts/perspectives/findings/details/FlaggedFindingUtils';
import { ExclusionRationaleModal } from 'ts/perspectives/findings/details/tabs/commons/ExclusionRationaleModal';
import { FindingsUtils } from 'ts/perspectives/findings/FindingsUtils';
import { openTaskSelectionModal } from 'ts/perspectives/quality_control/tasks/dialog/TaskSelectionDialog';
import type { Approved } from 'typedefs/Approved';
import { EFindingBlacklistType } from 'typedefs/EFindingBlacklistType';
import { EFindingBlacklistUpdateType } from 'typedefs/EFindingBlacklistUpdateType';
import type { FindingBlacklistInfo } from 'typedefs/FindingBlacklistInfo';
import type { IApprovalState } from 'typedefs/IApprovalState';
import type { Pending } from 'typedefs/Pending';
import type { TrackedFinding } from 'typedefs/TrackedFinding';
import type { User } from 'typedefs/User';
import type { UserResolvedFindingBlacklistInfo } from 'typedefs/UserResolvedFindingBlacklistInfo';

/** Props for the create task button consisting of commit, blacklist status and what findings to include */
type CreateTaskButtonProps = {
	commit: UnresolvedCommitDescriptor;
	blacklistInfo?: FindingBlacklistInfo[];
	findingsList: TrackedFinding[];
} & ButtonProps;

/** This button opens a dialog to add a list of findings to a task */
export function CreateTaskButton({ commit, blacklistInfo = [], findingsList, ...props }: CreateTaskButtonProps) {
	const projectId = useProjectId();
	const createTask = () => {
		const taskDetails = FindingsUtils.getTaskDataContainerForFindings(
			findingsList,
			commit,
			blacklistInfo.map(info => info.findingId)
		);
		openTaskSelectionModal(projectId, taskDetails);
	};
	return <Button icon="tasks" onClick={createTask} {...props} />;
}

/**
 * Provides a mutation to change the given findings with an optimistic update (the query cache is directly updated to
 * the new blacklist value instead of waiting on the server, if the update fails, the cache is reverted to the original
 * state)
 */
export function useBlacklistMutation(
	findingIDs: string[],
	commit: UnresolvedCommitDescriptor | null,
	onSuccess?: () => void,
	updateQueriesOptimistically = true
) {
	// Get the context
	const projectId = useProjectId();
	const user = useUserInfo().currentUser;
	const queryClient = useQueryClient();
	// Get the relevant queries for getting the status of single/ multiple flagged findings
	const singleFinding: Array<[string, QueryOperation<UserResolvedFindingBlacklistInfo | null>]> = findingIDs.map(
		findingID => [findingID, QUERY.getFlaggedFindingInfo(projectId, findingID, { t: commit ?? undefined })]
	);
	const allFindings = QUERY.getFlaggedFindingsInfos(projectId, { t: commit ?? undefined }, findingIDs);
	// Create the mutation to flag findings using optimistic updates
	return useMutationOperation(MUTATION.flagFindings, {
		onMutate: async updateRequest => {
			// Cancel any outgoing queries for flagged findings
			await Promise.all([
				queryClient.cancelQueries({ queryKey: allFindings.queryKey }),
				...singleFinding.map(([, q]) => queryClient.cancelQueries({ queryKey: q.queryKey }))
			]);
			// Save the current flagging state/ current query cache
			const previousBlacklistStatusAll = queryClient.getQueryData(allFindings.queryKey);
			const previousBlacklistStatusSingle = singleFinding.map(([, query]) => ({
				queryKey: query.queryKey,
				previousValue: queryClient.getQueryData(query.queryKey)
			}));

			if (!updateQueriesOptimistically) {
				return { previousBlacklistStatusAll, previousBlacklistStatusSingle };
			}
			// Update optimistically to the new flagging state (special case when remove --> set flagging to null)
			if (updateRequest.queryParams.operation === 'REMOVE') {
				queryClient.setQueryData(allFindings.queryKey, undefined);
				singleFinding.forEach(([, singleQuery]) => {
					queryClient.setQueryData(singleQuery.queryKey, null);
				});
			} else {
				// Update the query cache for single and multiple status requests
				const newBlacklistStatus: Omit<FindingBlacklistInfo, 'findingId'> = {
					type: updateRequest.queryParams.type ?? EFindingBlacklistType.FALSE_POSITIVE.name,
					approvalState: getExpectedApprovalState(user, updateRequest.body.autoApprove ?? false),
					user: user.username,
					rationale: updateRequest.body.blacklistInfo?.rationale ?? '',
					timestamp: new Date().getTime(),
					toleratedExternally: false
				};
				const newBlacklistStatusAll: FindingBlacklistInfo[] = updateRequest.body.findingIds.map(id => ({
					...ObjectUtils.deepClone(newBlacklistStatus),
					findingId: id
				}));
				queryClient.setQueryData(allFindings.queryKey, newBlacklistStatusAll);
				singleFinding.forEach(([findingId, singleQuery]) => {
					queryClient.setQueryData(singleQuery.queryKey, {
						...newBlacklistStatus,
						findingId,
						updateType: EFindingBlacklistUpdateType.ADD.name,
						resolvedUser: user,
						resolvedApprover: !isPending(newBlacklistStatus.approvalState) ? user : undefined
					});
				});
			}

			// Save the previous state in the context
			return { previousBlacklistStatusAll, previousBlacklistStatusSingle };
		},
		onSuccess,
		onError: (_err, _updateRequest, context) => {
			// On Error revert the optimistically made changes to the previous state using the context
			// @ts-ignore
			queryClient.setQueryData(allFindings.queryKey, context.previousBlacklistStatusAll);
			// @ts-ignore
			context.previousBlacklistStatusSingle.forEach(({ queryKey, previousValue }) => {
				queryClient.setQueryData(queryKey, previousValue);
			});
		},
		onSettled: () => {
			// In any case, if the update was successful or not, ensure that the next time, the state is actually fetched
			void allFindings.invalidate();
		}
	});
}

function getExpectedApprovalState(user: User, autoApprove: boolean): IApprovalState {
	if (autoApprove) {
		return {
			resolved: true,
			approved: true,
			by: user.username,
			at: new Date().getTime(),
			rationale: ''
		} satisfies Approved;
	}
	return {
		resolved: false
	} satisfies Pending;
}

/** The BlacklistButton Props consisting of commit, the findings to blacklist and what to do. */
type BlacklistButtonProps = {
	commit: UnresolvedCommitDescriptor;
	findingsList: TrackedFinding[];
	blackListType: EFindingBlacklistType;
} & ButtonProps;

/**
 * This button opens a dialog to flags a list of findings as either false-positive or tolerated depending on
 * {@link blackListType}
 */
export function BlacklistButton({ commit, findingsList, blackListType, ...props }: BlacklistButtonProps) {
	const [rationaleModalOpened, { open, close }] = useDisclosure(false);
	return (
		<>
			<Button
				icon={blackListType === EFindingBlacklistType.FALSE_POSITIVE ? 'false positive' : 'toleration'}
				onClick={open}
				{...props}
			/>
			{rationaleModalOpened ? (
				<ExclusionRationaleModal
					updateQueriesOptimistically
					onClose={close}
					exclusionType={blackListType}
					findingsToExclude={findingsList}
					commit={commit}
				/>
			) : null}
		</>
	);
}

/** The UnblacklistButton Props consisting of commit, the findings to unblacklist and their blacklist status. */
type UnblacklistButtonProps = {
	commit: UnresolvedCommitDescriptor;
	findingsList: TrackedFinding[];
	blacklistInfo: FindingBlacklistInfo | FindingBlacklistInfo[] | null;
} & ButtonProps;

/** This button removes a list of findings from the false-positive or tolerated flags */
export function UnblacklistButton({ commit, findingsList, blacklistInfo, ...props }: UnblacklistButtonProps) {
	const projectId = useProjectId();
	const blacklistInfoList = [blacklistInfo].flat();
	// The number of false positives, if zero, the list consists of findings with blacklist type: TOLERATION (used to determine the icon)
	const findingsFalsePositiveLength = blacklistInfoList.filter(finding => {
		if (finding) {
			return finding.type === EFindingBlacklistType.FALSE_POSITIVE.name;
		}
		return false;
	}).length;
	const findingIDsAll = findingsList.map(finding => finding.id);
	const mutationUnblacklistAll = useBlacklistMutation(findingIDsAll, commit);

	return (
		<Button
			icon={findingsList.length > 1 ? 'undo' : findingsFalsePositiveLength > 0 ? 'checkmark box' : 'eye'}
			onClick={() => {
				mutationUnblacklistAll.mutate({
					pathParams: {
						project: projectId
					},
					queryParams: { t: commit, operation: 'REMOVE' },
					body: {
						findingIds: findingIDsAll
					}
				});
			}}
			{...props}
		/>
	);
}
